import { LabeledValue, ModalOpenButton } from "@msys/ui";
import { Add as AddIcon, Edit as EditIcon } from "@mui/icons-material";
import { Box, Button, IconButton } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import React from "react";
import { CategorySearchModal } from "./CategorySearchModal.js";
import { CategoryLabeledValue } from "./CategoryLabeledValue.js";
import type { CategoryFragment } from "./useCategories.generated.js";

interface Props {
  categoryId: string | null;
  label?: string;
  selectButtonLabel?: string;
  leftIcon?: React.ReactNode;
  rightButton?: React.ReactNode;
  onChange: (category: CategoryFragment) => Promise<void> | void;
  onClear?: () => Promise<void> | void;
  required?: boolean;
  disabled?: boolean;
  canEdit?: boolean;
  alwaysShowLabel?: boolean;
}

export const CategoryInput = ({
  categoryId,
  label,
  selectButtonLabel,
  leftIcon,
  rightButton,
  required = false,
  disabled = false,
  canEdit = true,
  alwaysShowLabel = false,
  onChange,
  onClear,
}: Props) => {
  const { t } = useTranslate(["Categories", "Global"]);
  if (!canEdit) {
    return (
      <CategoryLabeledValue
        categoryId={categoryId}
        label={label}
        leftIcon={leftIcon}
      />
    );
  }

  if (!categoryId) {
    const button = (
      <ModalOpenButton
        Modal={CategorySearchModal}
        modalProps={{
          handleSelectCategory: async (category, handleClose) => {
            await onChange(category);
            handleClose();
          },
        }}
        disabled={disabled}
      >
        <Button
          size="small"
          color="secondary"
          variant="text"
          startIcon={<AddIcon />}
          disabled={disabled}
          sx={{ textWrap: "nowrap" }}
        >
          {selectButtonLabel ?? t("Add category", { ns: "Categories" })}
        </Button>
      </ModalOpenButton>
    );
    return alwaysShowLabel ? (
      <LabeledValue
        label={label ?? ""}
        sx={{ alignItems: "center", minHeight: "48px" }}
      >
        {button}
      </LabeledValue>
    ) : (
      <Box
        sx={{
          display: "flex",
        }}
      >
        {button}
      </Box>
    );
  }

  return (
    <CategoryLabeledValue
      categoryId={categoryId}
      label={label}
      leftIcon={leftIcon}
      required={required}
      rightButton={
        rightButton ?? (
          <ModalOpenButton
            Modal={CategorySearchModal}
            modalProps={{
              initialSelectedCategoryId: categoryId,
              handleSelectCategory: async (category, handleClose) => {
                await onChange(category);
                handleClose();
              },
              handleClearCategory: onClear
                ? async handleClose => {
                    await onClear();
                    handleClose();
                  }
                : undefined,
            }}
            disabled={disabled}
          >
            <IconButton size="small" color="primary" disabled={disabled}>
              <EditIcon />
            </IconButton>
          </ModalOpenButton>
        )
      }
    />
  );
};
